'use client'

import { useState, useEffect } from 'react'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { collection, query, where, getDocs, getFirestore, limit, orderBy, startAt, endAt } from 'firebase/firestore'

export default function JobSearch({ onSelect, placeholder = "Search for jobs", className = "" }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedJob, setSelectedJob] = useState(null)
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [searchError, setSearchError] = useState(null)

  useEffect(() => {
    const searchJobs = async () => {
      if (!searchQuery.trim()) {
        setSearchResults([])
        return
      }
      
      try {
        setIsSearching(true)
        setSearchError(null)
        const db = getFirestore()
        const jobsRef = collection(db, "jobs")
        
        // Search term
        const searchTermLower = searchQuery.toLowerCase()
        
        // Try multiple search strategies
        let results = []
        
        // Strategy 1: Try array-contains if titleLowercase is an array of tokens
        try {
          const arrayQuery = query(
            jobsRef, 
            where("titleLowercase", "array-contains", searchTermLower),
            limit(20)
          )
          const arraySnapshot = await getDocs(arrayQuery)
          
          if (!arraySnapshot.empty) {
            arraySnapshot.forEach((doc) => {
              results.push({
                id: doc.id,
                ...doc.data()
              })
            })
          }
        } catch (error) {
          console.log("Array search strategy failed:", error)
        }
        
        // Strategy 2: Try range queries on title field
        if (results.length === 0) {
          try {
            const titleQuery = query(
              jobsRef,
              orderBy("title"),
              startAt(searchQuery),
              endAt(searchQuery + '\uf8ff'),
              limit(20)
            )
            const titleSnapshot = await getDocs(titleQuery)
            
            titleSnapshot.forEach((doc) => {
              // Avoid duplicates
              if (!results.some(job => job.id === doc.id)) {
                results.push({
                  id: doc.id,
                  ...doc.data()
                })
              }
            })
          } catch (error) {
            console.log("Range search strategy failed:", error)
          }
        }
        
        // Strategy 3: Try a simple text query on title field (case insensitive comparison in JS)
        if (results.length === 0) {
          try {
            const allJobsQuery = query(jobsRef, limit(100))
            const allJobsSnapshot = await getDocs(allJobsQuery)
            
            allJobsSnapshot.forEach((doc) => {
              const data = doc.data()
              if (data.title && data.title.toLowerCase().includes(searchTermLower)) {
                results.push({
                  id: doc.id,
                  ...data
                })
              }
            })
          } catch (error) {
            console.log("All jobs search strategy failed:", error)
          }
        }
        
        // Store results
        setSearchResults(results)
        
        // Log if no results found
        if (results.length === 0) {
          console.log("No results found for query:", searchQuery)
          console.log("Try checking the job document structure in Firestore")
        }
      } catch (error) {
        console.error("Error searching jobs:", error)
        setSearchError(error.message)
      } finally {
        setIsSearching(false)
      }
    }

    // Debounce search to avoid too many requests
    const delaySearch = setTimeout(() => {
      searchJobs()
    }, 300)

    return () => clearTimeout(delaySearch)
  }, [searchQuery])

  const handleSelect = (job) => {
    setSelectedJob(job)
    if (onSelect) onSelect(job)
  }

  return (
    <Combobox
      as="div"
      value={selectedJob}
      onChange={handleSelect}
      className={className}
    >
      <div className="relative mt-2">
        <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </div>
        <ComboboxInput
          className="block w-full rounded-md bg-white py-1.5 pl-10 pr-12 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={placeholder}
          displayValue={(job) => job?.title}
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true" />
        </ComboboxButton>

        {isSearching && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-10">
            <svg className="animate-spin h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}

        {searchQuery && !isSearching && searchResults.length === 0 && (
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            <div className="px-4 py-2 text-sm text-gray-500">
              No jobs found matching '{searchQuery}'
            </div>
          </ComboboxOptions>
        )}

        {searchResults.length > 0 && (
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            {searchResults.map((job) => (
              <ComboboxOption
                key={job.id}
                value={job}
                className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-indigo-600 data-[focus]:text-white data-[focus]:outline-none"
              >
                <div className="flex flex-col">
                  <span className="truncate group-data-[selected]:font-semibold">{job.title}</span>
                  <span className="truncate text-gray-500 group-data-[focus]:text-indigo-200">
                    {job.company} {job.location ? `• ${job.location}` : ''}
                  </span>
                </div>

                <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-indigo-600 group-data-[selected]:flex group-data-[focus]:text-white">
                  <CheckIcon className="size-5" aria-hidden="true" />
                </span>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}

        {searchError && (
          <div className="absolute mt-1 w-full text-xs text-red-500">
            Error searching: {searchError}
          </div>
        )}
      </div>
    </Combobox>
  )
} 